/**
 * AssetDocPreview - the shared, inline document renderer.
 *
 * Generic (not SOW-specific): given a workspace.asset uuid it fetches the bytes and
 * renders by mime type - .docx via docx-preview (real fonts/colors/layout), images
 * inline, everything else as a text excerpt + download fallback. It can also render
 * inline markdown when there is no asset (e.g. an in-run drafted template that has no
 * persisted asset yet).
 *
 * Two modes:
 * - 'full'    - render the whole document, optionally scrolled to a heading.
 * - 'section' - clip the render to a single section, between `heading` and
 *               `endHeading` (both supplied by the caller, so the clip is
 *               deterministic and independent of heading-style detection).
 *
 * Lazy: nothing is fetched or parsed until `active` is true (the visible form tab /
 * the expanded card), so collapsed previews cost nothing.
 *
 * Consumers: FilePreviewDialog (modal, full), UserInteractionDrawer (section tab,
 * section), AiStructuredMessage document_preview card (full + markdown).
 */

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { useProtectedBlob } from '../../../hooks/useProtectedBlob';
import { useWorkspaceAssetMeta } from '../../../hooks/useWorkspaceAssetMeta';

export const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Base class handed to docx-preview - styles are injected scoped to it. */
const DOCX_CLASS = 'ai-file-preview-docx';

/** Block-level elements docx-preview emits, queried in document order for clipping. */
const BLOCK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, table, ul, ol';

function normalizeText(s: string): string {
  return (s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function matchesHeading(text: string, heading: string): boolean {
  const t = normalizeText(text);
  const h = normalizeText(heading);
  return !!t && !!h && (t === h || t.startsWith(h));
}

/** Rendered font size + numeric weight of an element (handles 'bold'/'normal'). */
function fontMetrics(el: HTMLElement): { size: number; weight: number } {
  const cs = window.getComputedStyle(el);
  const size = parseFloat(cs.fontSize) || 0;
  let weight = parseInt(cs.fontWeight, 10);
  if (Number.isNaN(weight)) weight = cs.fontWeight === 'bold' ? 700 : 400;
  return { size, weight };
}

/**
 * Heuristic for "this block is a peer section heading" - same-or-larger and bold
 * relative to the section's own heading. Used to bound the clip when the next
 * heading's text wasn't supplied or didn't match, so the section never bleeds into
 * the rest of the document.
 */
function looksLikePeerHeading(el: HTMLElement, ref: { size: number; weight: number }): boolean {
  const m = fontMetrics(el);
  return m.weight >= 600 && m.size >= ref.size - 0.5;
}

/**
 * Best-effort scroll: find the first rendered line whose trimmed text matches the
 * heading (case-insensitive, exact-or-prefix) and bring it into view. Silent no-op
 * when nothing matches, so the whole-document preview always works.
 */
export function scrollToHeadingIn(host: HTMLElement, heading: string): void {
  const target = heading.trim().toLowerCase();
  if (!target) return;
  const lines = host.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, p');
  for (const el of Array.from(lines)) {
    const text = (el.textContent ?? '').trim().toLowerCase();
    if (text && (text === target || text.startsWith(target))) {
      el.scrollIntoView({ block: 'start' });
      return;
    }
  }
}

/**
 * Clip the rendered docx to a single section: keep the block elements from the one
 * matching `heading` up to (but not including) the one matching `endHeading` (or the
 * end of the document). Returns false when the start heading is not found, so the
 * caller can fall back to the full render. Preserves docx-preview's injected
 * stylesheet so fonts/colors survive the clip.
 */
function clipToSection(host: HTMLElement, heading: string, endHeading?: string): boolean {
  const blocks = Array.from(host.querySelectorAll<HTMLElement>(BLOCK_SELECTOR));
  if (blocks.length === 0) return false;
  const start = blocks.findIndex((el) => matchesHeading(el.textContent ?? '', heading));
  if (start === -1) return false;

  // Bound the section at the next heading. Prefer an exact text match against the
  // next section's heading; fall back to the first peer-looking heading so a missing
  // or slightly-mismatched endHeading never lets the section bleed to end-of-document.
  const ref = fontMetrics(blocks[start]!);
  let end = blocks.length;
  for (let i = start + 1; i < blocks.length; i++) {
    const el = blocks[i]!;
    if (endHeading && matchesHeading(el.textContent ?? '', endHeading)) {
      end = i;
      break;
    }
    if (looksLikePeerHeading(el, ref)) {
      end = i;
      break;
    }
  }

  const selected = blocks.slice(start, end);
  // Drop nodes nested inside another selected node (e.g. <p> inside a selected
  // <table> cell, <li> inside a selected <ul>) so each block is cloned once.
  const top = selected.filter(
    (el) => !selected.some((other) => other !== el && other.contains(el))
  );
  if (top.length === 0) return false;

  const wrapper = document.createElement('div');
  wrapper.className = `${DOCX_CLASS}-wrapper`;
  // Keep docx-preview's injected <style> blocks (scoped to .ai-file-preview-docx).
  host.querySelectorAll('style').forEach((s) => wrapper.appendChild(s.cloneNode(true)));
  const section = document.createElement('section');
  section.className = DOCX_CLASS;
  for (const el of top) section.appendChild(el.cloneNode(true));
  wrapper.appendChild(section);
  host.replaceChildren(wrapper);
  return true;
}

/** Renders a .docx faithfully into a scrollable host using docx-preview (lazy-loaded). */
function DocxPreview({
  blob,
  scrollToHeading,
  clipHeading,
  clipEndHeading,
  fallbackText,
  fit,
}: {
  blob: Blob;
  scrollToHeading?: string;
  clipHeading?: string;
  clipEndHeading?: string;
  fallbackText?: string;
  /** Flow the content to the container width instead of a fixed page sheet (inline/cards). */
  fit?: boolean;
}) {
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [status, setStatus] = React.useState<'rendering' | 'ready' | 'error'>('rendering');
  const clip = !!clipHeading;

  React.useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    if (!host) return;
    host.innerHTML = '';
    setStatus('rendering');
    void (async () => {
      try {
        const { renderAsync } = await import('docx-preview');
        if (cancelled || !hostRef.current) return;
        await renderAsync(blob, hostRef.current, undefined, {
          className: DOCX_CLASS,
          // Fit mode (inline pane / chat cards): drop the grey page wrapper and the
          // fixed page width/height so the content flows to the container width.
          // Otherwise (the wide modal) keep the faithful page sheet.
          inWrapper: !fit,
          ignoreWidth: !!fit,
          ignoreHeight: !!fit,
          // Clipping wants one continuous flow; page chrome only for full renders.
          breakPages: !clip,
          renderHeaders: !clip,
          renderFooters: !clip,
        });
        if (cancelled || !hostRef.current) return;
        if (clip && clipHeading) {
          const clipped = clipToSection(hostRef.current, clipHeading, clipEndHeading);
          if (!clipped) {
            // Section start not found: show the text excerpt rather than the whole
            // document (the pane is meant to show only this section).
            if (fallbackText) {
              hostRef.current.innerHTML = '';
              setStatus('error');
              return;
            }
            scrollToHeadingIn(hostRef.current, clipHeading);
          }
        } else if (scrollToHeading) {
          scrollToHeadingIn(hostRef.current, scrollToHeading);
        }
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blob, scrollToHeading, clip, clipHeading, clipEndHeading, fit, fallbackText]);

  return (
    <>
      {status === 'rendering' ? (
        <p className="ai-file-preview__status">Rendering document…</p>
      ) : null}
      {status === 'error' ? (
        fallbackText ? (
          <pre className="ai-doc-preview__fallback-text">{fallbackText}</pre>
        ) : (
          <p className="ai-file-preview__status">This document could not be rendered.</p>
        )
      ) : null}
      <div ref={hostRef} className="ai-file-preview__docx-host" />
    </>
  );
}

function FallbackBody({
  fallbackText,
  fileName,
  objectUrl,
}: {
  fallbackText?: string;
  fileName: string;
  objectUrl: string | null;
}) {
  return (
    <div className="ai-file-preview__fallback">
      {fallbackText ? (
        <pre className="ai-doc-preview__fallback-text">{fallbackText}</pre>
      ) : (
        <p className="ai-file-preview__status">
          Inline preview isn’t available for this file type.
        </p>
      )}
      {objectUrl ? (
        <a className="ai-chat-btn" href={objectUrl} download={fileName}>
          Download {fileName}
        </a>
      ) : null}
    </div>
  );
}

export interface AssetDocPreviewProps {
  /** Asset to render. null (with no markdown) renders nothing. */
  assetUuid: string | null;
  /** Lazy gate: only fetch + parse when true (visible tab / expanded card). */
  active: boolean;
  /** Header / download name; also used to detect .docx by extension. */
  fileName?: string;
  /** Monospace excerpt shown for PDF / non-docx / error / no-asset. */
  fallbackText?: string;
  /** Rendered when there is no `assetUuid` (e.g. an in-run drafted template). */
  markdownContent?: string;
  /** 'full' renders the whole doc; 'section' clips to [heading..endHeading). */
  mode?: 'full' | 'section';
  /** Section start heading (clip start in 'section' mode; scroll target in 'full'). */
  heading?: string;
  /** Next section's heading - clip boundary in 'section' mode. */
  endHeading?: string;
  /**
   * Flow a .docx to the container width (no fixed page sheet / grey wrapper chrome) -
   * for the narrow inline pane and chat cards. Omit for the wide modal, which keeps the
   * faithful, page-accurate render.
   */
  fit?: boolean;
}

export function AssetDocPreview({
  assetUuid,
  active,
  fileName,
  fallbackText,
  markdownContent,
  mode = 'full',
  heading,
  endHeading,
  fit,
}: AssetDocPreviewProps) {
  const hasAsset = !!assetUuid;
  // Only fetch while active - collapsed cards / inactive tabs cost nothing.
  const activeUuid = active && hasAsset ? assetUuid : null;
  const meta = useWorkspaceAssetMeta(activeUuid);
  const contentUrl = activeUuid ? `/api/workspace-assets/${activeUuid}/content` : null;
  const { blob, loading, error } = useProtectedBlob(contentUrl);
  const objectUrl = React.useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  React.useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  if (!active) return null;

  if (!hasAsset) {
    if (markdownContent) {
      return (
        <div className="ai-doc-preview ai-doc-preview--markdown">
          <ReactMarkdown>{markdownContent}</ReactMarkdown>
        </div>
      );
    }
    return fallbackText ? (
      <pre className="ai-doc-preview__fallback-text">{fallbackText}</pre>
    ) : null;
  }

  if (loading) return <p className="ai-file-preview__status">Loading…</p>;
  if (error || !blob) {
    return (
      <FallbackBody
        fallbackText={fallbackText}
        fileName={fileName ?? 'file'}
        objectUrl={objectUrl}
      />
    );
  }

  const mimeType = meta?.mimeType;
  const name = fileName ?? meta?.originalFileName ?? 'document';
  const isImage = mimeType?.startsWith('image/') ?? false;
  const isDocx = mimeType === DOCX_MIME || name.toLowerCase().endsWith('.docx');

  if (isImage && objectUrl) {
    return <img className="ai-doc-preview__image" src={objectUrl} alt={name} />;
  }
  if (isDocx) {
    return (
      <div className="ai-doc-preview ai-doc-preview--docx">
        <DocxPreview
          blob={blob}
          scrollToHeading={mode === 'full' ? heading : undefined}
          clipHeading={mode === 'section' ? heading : undefined}
          clipEndHeading={mode === 'section' ? endHeading : undefined}
          fallbackText={fallbackText}
          fit={fit}
        />
      </div>
    );
  }
  return <FallbackBody fallbackText={fallbackText} fileName={name} objectUrl={objectUrl} />;
}
